import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, timeout } from 'rxjs';
import { environment } from '@deltager/organizers/admin/environments/environment';
import { MatDialog } from '@angular/material/dialog';
import { OnboardDialogComponent, OnboardDialogComponentData } from './onboard-dialog/onboard-dialog.component';
import { UserService } from '../../core/services/user.service';
import { EventType } from '../../shared/models/EventType';
import { PaymentType } from '../../shared/models/PaymentType';
import { ChangeEventStatusAction } from '../events/create-event/store/event-setup.actions';
import { EventSetupState, EventStatus } from '../events/create-event/store/event-setup.state';
import { Store } from '@ngrx/store';
import { AppSettingsService } from '../../core/app-settings/app-settings.service';

class GetUrlResult {
  url: string;
}

class OnboardingUrlPayload {
  constructor(
    returnUrl: string,
    refreshUrl: string,
    paymentType?: PaymentType,
    eventId?: number,
    eventType?: EventType,
  ) {
    this.returnUrl = returnUrl;
    this.refreshUrl = refreshUrl;
    this.paymentType = paymentType;
    this.eventId = eventId;
    this.eventType = eventType;
  }

  returnUrl: string;
  refreshUrl: string;
  createAccount: boolean;
  paymentType?: PaymentType;
  eventId?: number;
  eventType?: EventType;
}

export class PayoutDelayDaysLimits {
  minimum: number;
  maximum: number;
}

export class PaymentServiceProviderStatus {
  paymentType: PaymentType;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  payoutDelayDays?: number;
  needsABankAccount: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class PaymentServiceProviderService {
  private readonly resource = 'payment-service-provider';

  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private userService: UserService,
    private store: Store<{ event: EventSetupState }>,
    private appSettingsService: AppSettingsService,
  ) {}

  async onboard(eventId: number, eventType: EventType, returnUrl: string): Promise<void> {
    const status = await this.getProviderStatus(eventId, eventType);
    const data: OnboardDialogComponentData = { returnUrl, eventId, eventType, status };
    var ref = this.dialog.open(OnboardDialogComponent, { data, width: '100%', minWidth: '250px', maxWidth: '600px', autoFocus: false,
      maxHeight: '90vh' });

    ref.afterClosed().subscribe(() => {
      var updateStatusAction = new ChangeEventStatusAction({ status: EventStatus.submitted });
      this.store.dispatch(updateStatusAction);
    });
  }

  async redirectToOnboardingPageViaPaymentType(paymentType: PaymentType, returnUrl?: string): Promise<void> {
    await this.redirectToOnboardingPage(paymentType, undefined, undefined, returnUrl);
  }

  private async redirectToOnboardingPage(
    paymentType: PaymentType,
    eventId: number,
    eventType: EventType,
    returnUrl?: string,
  ): Promise<void> {
    const organizerId = this.userService.OrganizerId;

    const refreshUrl = `${this.appSettingsService.settingsValue.rootUrl}/arrangement/onboard.aspx?organizerId=${organizerId}`;
    returnUrl = returnUrl ?? window.location.href;
    const payload = new OnboardingUrlPayload(returnUrl, refreshUrl, paymentType, eventId, eventType);

    const result = await firstValueFrom(
      this.http.post<GetUrlResult>(`${environment.apiBaseURL}/${this.resource}/${organizerId}/onboard-url`, payload),
    );

    window.location.href = result.url;
  }

  getProviderStatus(eventId: number, eventType: EventType): Promise<PaymentServiceProviderStatus> {
    let params = new HttpParams();
    params = params.set('eventId', eventId);
    params = params.set('type', eventType);

    return firstValueFrom(
      this.http.get<PaymentServiceProviderStatus>(`${environment.apiBaseURL}/${this.resource}/status`, { params }),
    );
  }

  getStatus(timeoutMs?: number): Promise<PaymentServiceProviderStatus[]> {
    const organizerId = this.userService.OrganizerId;

    const request$ = this.http.get<PaymentServiceProviderStatus[]>(
      `${environment.apiBaseURL}/${this.resource}/${organizerId}/status`,
    );

    // The rxjs timeout unsubscribes, which aborts the underlying request — a plain promise race
    // would stop awaiting but leave a hung XHR open to accumulate.
    return firstValueFrom(timeoutMs ? request$.pipe(timeout(timeoutMs)) : request$);
  }

  /**
   * Whether this organizer's payments run through Adyen. Fails open (false) so a transient
   * status failure never locks legacy-rail organizers out of surfaces gated on this. The timeout
   * routes a hung request through the same fail-open path — without it, an accepted-but-never-
   * completing status call leaves every consumer of this check without a terminal state.
   */
  async isAdyenRouted(): Promise<boolean> {
    try {
      const statuses = await this.getStatus(10_000);
      return statuses?.some(s => s.paymentType === PaymentType.Adyen) ?? false;
    } catch {
      return false;
    }
  }

  isOnboardingRequired(eventId: number, type: EventType): Promise<boolean> {
    let params = new HttpParams();
    params = params.set('type', type);

    return firstValueFrom(
      this.http.get<boolean>(`${environment.apiBaseURL}/${this.resource}/${eventId}/is-onboarding-required`, {
        params,
      }),
    );
  }

  getObjectPayoutDelayDaysLimits(id: number, type: EventType): Promise<PayoutDelayDaysLimits> {
    let params = new HttpParams();
    params = params.set('type', type);
    params = params.set('id', id);

    return firstValueFrom(
      this.http.get<PayoutDelayDaysLimits>(
        `${environment.apiBaseURL}/${this.resource}/payout-delay-days-limits/object`,
        { params },
      ),
    );
  }

  getOrganizerPayoutDelayDaysLimits(id: number): Promise<PayoutDelayDaysLimits> {
    let params = new HttpParams();
    params = params.set('id', id);

    return firstValueFrom(
      this.http.get<PayoutDelayDaysLimits>(
        `${environment.apiBaseURL}/${this.resource}/payout-delay-days-limits/organizer`,
        { params },
      ),
    );
  }
}
